import { useRef } from 'react';
import { PaperclipIcon } from '@/shared/ui/atoms';
import { useToastStore } from '@/shared/lib/toast';
import {
  validateFile,
  getSignedUploadUrl,
  uploadFileToStorage,
  ALLOWED_MIME_TYPES,
} from '@/shared/api';
import { getApiErrorMessage } from '@/shared/lib/getApiErrorMessage';
import { useAddAttachment } from '../../model/useAttachments';
import * as s from './FileAttachmentArea.css';

export interface AttachFileButtonProps {
  workLogId: string | undefined;
}

/** 에디터 툴바 끝에 얹는 파일 추가 아이콘 버튼. 첨부 목록은 FileAttachmentArea 가 그린다. */
export function AttachFileButton({ workLogId }: AttachFileButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const addMutation = useAddAttachment();

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !workLogId) return;

    try {
      const mimeType = validateFile(file);
      const { signedUrl, path } = await getSignedUploadUrl(
        file.name,
        mimeType,
        'worklog-attachments'
      );
      await uploadFileToStorage(signedUrl, file, mimeType);

      addMutation.mutate(
        {
          workLogId,
          dto: { path, filename: file.name, mimeType, size: file.size },
        },
        {
          onError: (error) => {
            useToastStore.getState().addToast({
              title: '첨부파일 등록 실패',
              body: getApiErrorMessage(error, '잠시 후 다시 시도해주세요.'),
              type: 'error',
            });
          },
        }
      );
    } catch (error) {
      useToastStore.getState().addToast({
        title: '파일 업로드 실패',
        body: error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.',
        type: 'error',
      });
    }

    if (inputRef.current) inputRef.current.value = '';
  }

  if (!workLogId) return null;

  return (
    <>
      <button
        type="button"
        className={s.attachButton}
        onClick={() => inputRef.current?.click()}
        aria-label="파일 추가"
        title="파일 추가"
      >
        <PaperclipIcon size={18} />
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_MIME_TYPES.join(',')}
        // 보이는 라벨이 없는 숨김 input — 이름이 없으면 label 위반이다.
        aria-label="첨부 파일 선택"
        className={s.hiddenInput}
        onChange={(e) => {
          void handleFileSelect(e);
        }}
        tabIndex={-1}
      />
    </>
  );
}
